import type { CustomerAddressDTO } from "@/modules/customers/domain/contracts";
import type {
  PosCheckoutBody,
  PosQuickCreateCustomerBody,
  PosQuickCreateCustomerResult,
  RecordSocialTransferPaymentBody,
} from "@/modules/pos/domain/contracts";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type { CustomerAddressDTO } from "@/modules/customers/domain/contracts";
export type {
  PosCheckoutBody,
  PosQuickCreateCustomerBody,
  PosQuickCreateCustomerResult,
  RecordSocialTransferPaymentBody,
} from "@/modules/pos/domain/contracts";

export type PosCheckoutResult = {
  orderId: string;
  orderCode: string;
};

export type RecordSocialTransferPaymentResponse = {
  ok: true;
  orderId: string;
};

export type PosHalProvinceOption = {
  id: string;
  name: string;
};

export type PosHalDistrictOption = {
  id: string;
  name: string;
};

export type PosHalBranchOption = {
  id: number;
  code: string;
  name: string;
  provinceName?: string;
  districtName?: string;
  villageName?: string;
};

export const posApi = {
  async checkout(body: PosCheckoutBody): Promise<PosCheckoutResult> {
    return fetcher.post<PosCheckoutResult>(
      `${config.apiUrl}/pos/checkout`,
      body,
    );
  },

  async quickCreateCustomer(
    body: PosQuickCreateCustomerBody,
  ): Promise<PosQuickCreateCustomerResult> {
    return fetcher.post<PosQuickCreateCustomerResult>(
      `${config.apiUrl}/pos/customers/quick`,
      body,
    );
  },

  async listCustomerAddresses(
    customerId: string,
  ): Promise<CustomerAddressDTO[]> {
    return fetcher.get<CustomerAddressDTO[]>(
      `${config.apiUrl}/customers/${customerId}/addresses`,
    );
  },

  async recordSocialTransferPayment(
    body: RecordSocialTransferPaymentBody,
  ): Promise<RecordSocialTransferPaymentResponse> {
    return fetcher.post<RecordSocialTransferPaymentResponse>(
      `${config.apiUrl}/pos/social/record-transfer-payment`,
      body,
    );
  },

  async listHalProvinces(): Promise<PosHalProvinceOption[]> {
    return fetcher.get<PosHalProvinceOption[]>(
      `${config.apiUrl}/pos/hal/provinces`,
    );
  },

  async listHalDistricts(provinceId: string): Promise<PosHalDistrictOption[]> {
    return fetcher.get<PosHalDistrictOption[]>(
      `${config.apiUrl}/pos/hal/districts?provinceId=${encodeURIComponent(provinceId)}`,
    );
  },

  async listHalBranches(input: {
    provinceId?: string | null;
    districtId?: string | null;
    search?: string | null;
  }): Promise<PosHalBranchOption[]> {
    const params = new URLSearchParams();
    if (input.provinceId) params.set("provinceId", input.provinceId);
    if (input.districtId) params.set("districtId", input.districtId);
    if (input.search?.trim()) params.set("search", input.search.trim());
    const query = params.toString();
    return fetcher.get<PosHalBranchOption[]>(
      `${config.apiUrl}/pos/hal/branches${query ? `?${query}` : ""}`,
    );
  },
};
